/**
 * Perfume grid carousel — see sections/perfume-grid.liquid.
 *
 * The card row is a horizontal scroll-snap track at every breakpoint (all of
 * the sliding itself is native CSS); this element only adds the arrows, the
 * dots and their state. It hides both whenever the track does not actually
 * overflow — so "5 cards per view" with 5 cards stays a plain row.
 *
 * Dots are generated here, ONE PER PAGE (a page = one viewport of cards), not
 * one per card: with 9 cards at 5 per view there are 2 dots, because there are
 * only 2 scroll positions. They are rebuilt whenever the page count changes
 * (resize, breakpoint change, images laying out late).
 *
 * A custom element so it re-initialises after Theme Editor section re-renders.
 */
class PerfumeCarousel extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-carousel-track]');
    if (!this.list) return;

    this.dotsContainer = this.querySelector('[data-carousel-dots]');
    this.dots = [];
    this.previous = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');

    this.update = this.update.bind(this);
    this.onScroll = this.throttle(this.update);

    this.list.addEventListener('scroll', this.onScroll, { passive: true });
    this.previous?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));

    // Catches both viewport resizes and the track being laid out late (images).
    this.resizeObserver = new ResizeObserver(this.onScroll);
    this.resizeObserver.observe(this.list);

    this.update();
  }

  disconnectedCallback() {
    this.list?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  throttle(callback) {
    let frame = null;
    return () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        callback();
      });
    };
  }

  /** One page = one viewport width of cards. 2px of slack: sub-pixel widths
   *  otherwise report a permanent 1px overflow and invent an extra page. */
  get pageCount() {
    const page = this.list.clientWidth;
    if (!page) return 1;
    return Math.max(1, Math.ceil((this.list.scrollWidth - 2) / page));
  }

  /** Position expressed as a page index, mapped across the ACTUAL scrollable
   *  range rather than raw viewport widths. With N+1 cards at N per view, the
   *  whole scrollable distance is only a fraction of one viewport (one extra
   *  card, not a full page), so rounding scrollLeft / clientWidth always came
   *  out to 0 and the second dot never lit up even once fully scrolled. */
  get activePage() {
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    const count = this.pageCount;
    if (maxScroll <= 0 || count <= 1) return 0;
    const ratio = this.list.scrollLeft / maxScroll;
    return Math.min(count - 1, Math.max(0, Math.round(ratio * (count - 1))));
  }

  step(direction) {
    this.list.scrollBy({ left: direction * this.list.clientWidth, behavior: 'smooth' });
  }

  /** Distributes pages evenly across the real scrollable range so a dot always
   *  lands exactly on its page, whether the overflow is many full viewports or
   *  just part of one (see activePage above). */
  scrollToPage(index) {
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    const count = this.pageCount;
    const target = count > 1 ? (maxScroll * index) / (count - 1) : 0;
    this.list.scrollTo({ left: target, behavior: 'smooth' });
  }

  /** Rebuilt only when the number of pages actually changes, so scrolling and
   *  the ResizeObserver do not thrash the DOM. */
  renderDots(count) {
    if (!this.dotsContainer || this.dots.length === count) return;

    this.dotsContainer.textContent = '';
    this.dots = [];
    if (count < 2) return;

    for (let index = 0; index < count; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'perfume-grid__dot';
      dot.setAttribute('data-carousel-dot', '');
      dot.setAttribute('aria-label', `${index + 1} / ${count}`);
      dot.addEventListener('click', () => this.scrollToPage(index));
      this.dotsContainer.appendChild(dot);
      this.dots.push(dot);
    }
  }

  update() {
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    const hasOverflow = maxScroll > 2;

    this.toggleAttribute('data-carousel-active', hasOverflow);
    this.renderDots(hasOverflow ? this.pageCount : 0);

    if (this.previous) this.previous.disabled = !hasOverflow || this.list.scrollLeft <= 2;
    if (this.next) this.next.disabled = !hasOverflow || this.list.scrollLeft >= maxScroll - 2;

    const active = this.activePage;
    this.dots.forEach((dot, index) => {
      const isActive = index === active;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('perfume-carousel')) {
  customElements.define('perfume-carousel', PerfumeCarousel);
}
